import {DaysListCol} from '@components/competitions/levels/muharram/DaysListCol';
import {appConfig} from '@config';
import {useCompetitionAhdContext} from '@context/CompetitionAhdContext';
import {Box, CircularProgress, useTheme} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import socketCompetition from '@sockets/competition';
import {isValidPhoneNumber} from '@utils';
import axios from 'axios';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import ReactPlayer from 'react-player';
import ahdContent from './assets/images/1.webp';
import telBg from './assets/images/4.webp';
import ahd1 from './assets/images/ahd-1.webp';
import ahd2 from './assets/images/ahd-2.webp';
import gonbad from './assets/images/ahd-gonbad.webp';
import count from './assets/images/count.webp';
import tel from './assets/images/tel.webp';

type IProps = {
	state?: string
};

type LastData = {
	competition: number;
	phone: string;
};
const defaultUrl = 'https://files.novinrazavi.ir/competition/ahd.mp4';

export default function Mission0(props: IProps) {
	const theme = useTheme();
	const {t} = useTranslation();
	const [loading, setLoading] = useState(false);
	const [remaining, setRemaining] = useState(0);
	const [counter, setCounter] = useState(0);
	const [videoEnd, setVideoEnd] = useState(false);
	const [phoneValid, setPhoneValid] = useState<boolean | null>(null);
	const [dataMessage, setDataMessage] = useState('');
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const player = useRef<ReactPlayer>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [snackSeverity, setSnackSeverity] = useState<'error' | 'success' | 'info' | 'warning' | undefined>('info');
	const [url, setUrl] = useState(defaultUrl);
	const [muted, setMuted] = useState(true);
	const playerContainer = useRef<HTMLDivElement>(null);
	const phoneInputRef = useRef<HTMLInputElement>(null);
	const {
		setPhone,
		phone,
		setMission
	} = useCompetitionAhdContext();
	
	useEffect(() => {
		socketCompetition.connect();
		
		socketCompetition.on('ahd:count', (data: {count: number}) => {
			// setState if you want to render it in UI
			setCounter(data.count);
		});
		
		return () => {
			socketCompetition.off('ahd:count');
			socketCompetition.disconnect();
		};
	}, []);
	
	const sendData = useCallback(async (lastData: LastData) => {
		if (!isValidPhoneNumber(lastData.phone)) {
			setPhoneValid(false);
			setDataMessage('شماره تلفن صحیح نمی باشد');
			setSnackSeverity('error');
			setSnackbarOpen(true);
			return;
		}
		
		const controller = new AbortController();
		const timeoutId = setTimeout(() => {
			controller.abort(); // cancel request
		}, 10000); // ⏱️ 10 sec timeout (adjust as needed)
		
		try {
			setLoading(true);
			setPhoneValid(true);
			
			const res = await axios.post(
				`${appConfig.competition2.url}ahd`,
				lastData,
				{signal: controller.signal}
			);
			
			if (!res.data.success && res.data.code === 'PHONE_EXISTS') {
				setDataMessage('عهد شما قبلاً ثبت شده است.');
				setSnackSeverity('warning');
			} else {
				setDataMessage('عهد شما ثبت شد');
				setSnackSeverity('info');
				setPhone('');
			}
			setCounter(res.data.result.totalCount);
			setSnackbarOpen(true);
		} catch (err: any) {
			if (axios.isCancel(err)) {
				setDataMessage('درخواست بیش از حد طول کشید، دوباره تلاش کنید.');
			} else {
				const serverError = err.response?.data?.message || 'ارسال اطلاعات با خطا مواجه شد.';
				setDataMessage(serverError);
			}
			setSnackSeverity('error');
			setSnackbarOpen(true);
		} finally {
			clearTimeout(timeoutId);
			setLoading(false);
		}
	}, []);
	
	const handleSubmit = () => {
		if (!phone?.trim()) {
			setDataMessage('لطفاً شماره همراه خود را کامل کنید.');
			setSnackSeverity('warning');
			setPhoneValid(false); // ❌ mark invalid
			setSnackbarOpen(true);
			
			// 👉 Focus input
			phoneInputRef.current?.focus();
			return;
		}
		if (!isValidPhoneNumber(phone)) {
			setPhoneValid(false);
			setDataMessage('شماره موبایل معتبر نیست.');
			setSnackSeverity('error');
			setSnackbarOpen(true);
			
			// 👉 Focus input
			phoneInputRef.current?.focus();
			return;
		}
		
		setDataMessage('');
		setPhoneValid(true);
		
		const lastData: LastData = {
			competition: 6,
			phone
		};
		sendData(lastData);
	};
	
	function onEnded() {
		setVideoEnd(true);
		setIsPlaying(false);
	}
	
	const onPlayClick = () => {
		// props.playerRef?
		setIsPlaying(() => !isPlaying);
	};
	
	return (
		<>
			<Snackbar
				open={snackbarOpen}
				autoHideDuration={5000}
				onClose={() => setSnackbarOpen(false)}
				anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
			>
				<MuiAlert
					onClose={() => setSnackbarOpen(false)}
					severity={snackSeverity}
					sx={{width: '100%'}}
				>
					{dataMessage}
				</MuiAlert>
			</Snackbar>
			
			<div className="w-full h-screen flex flex-col items-start justify-start overflow-y-scroll relative gap-y-3 pb-10">
				<div className={'w-full h-fit flex flex-row justify-center items-end px-5 pt-5 gap-x-0'}>
					<div className={'flex flex-col items-center gap-y-0'}>
						<img
							src={ahd1}
							alt="ahd"
							className="w-fit h-fit cursor-pointer"
						/>
						<img
							src={ahd2}
							alt="ahd"
							className="w-fit h-fit cursor-pointer px-4 pt- pb-4"
						/>
					</div>
					<img
						src={gonbad}
						alt="ahd"
						className="w-5/12 h-fit cursor-pointer"
					/>
				</div>
				<div className={'w-full flex flex-row justify-center items-center'}>
					<img
						src={ahdContent}
						alt="ahdContent"
						className="w-7/12 h-fit cursor-pointer"
					/>
				</div>
				<div className={'w-full flex flex-col justify-center items-center gap-x-10'}>
					{!loading ? (
						<>
							<div className={'text-white font-samir'}>شماره همراه خود را وارد کنید:</div>
							<div className="relative w-36 h-16 text-black placeholder-black">
								<img src={tel} alt="tphone"
								     className={`absolute w-full h-full object-contain ${phoneValid === false && 'animate-shake'}`}
								/>;
								<input
									type="tel"
									placeholder="شماره همراه"
									value={phone || '09'}
									maxLength={11} // Iran mobile numbers length
									onChange={(e) => {
										let val = e.target.value;
										
										// always start with '09'
										if (!val.startsWith('09')) {
											val = '09' + val.replace(/^0*/, '');
										}
										
										// allow only digits
										val = val.replace(/\D/g, '');
										
										setPhone(val);
									}}
									ref={phoneInputRef}
									className={`
								    font-samirNum font-bold absolute top-0 left-0 w-full h-full text-center
								    bg-transparent text-black focus:outline-none phone-font
								    ${phoneValid === false ? 'animate-shake rounded-md' : ''}
								  `}
								/>
							</div>
							<div className={'w-full h-fit flex flex-row gap-x-2 justify-center items-center'}>
								<div className={''}>
									<img
										src={telBg}
										alt="tel"
										className="w-36 h-fit cursor-pointer"
										onClick={handleSubmit}
									/>
								</div>
								<div
									style={{backgroundImage: `url(${count})`}}
									className="font-samirNum font-bold w-36 h-12 text-center text-white bg-no-repeat bg-contain justify-center items-center"
								>
									<div className={'h-12 text-center justify-center items-center flex flex-row text-xl'}>
										<span>{counter}</span>
										<span> عهد </span>
									</div>
								</div>
							</div>
						</>
					) : (
						<div className="w-full h-16 flex justify-center items-center">
							<CircularProgress sx={{color: '#fff'}} size={30}/>
						</div>
					)}
				</div>
				
				<Box className="w-full flex flex-col justify-center items-center">
					<div className="w-8/12 h-fit mx-auto">
						{/*<MaximizableView*/}
						{/*	backgroundColor="#efefef"*/}
						{/*	buttonColor={'#be8730'}*/}
						{/*	remaining={remaining}*/}
						{/*	videoEnd={videoEnd}*/}
						{/*>*/}
						<div
							className="w-full h-full flex flex-row justify-center items-center rounded-xl border-gray-500 border p-1 relative"
							ref={playerContainer}
						>
							<ReactPlayer
								width={'100%'}
								height={'100%'}
								ref={player}
								controls={true}
								className="react-player outline-none rounded-xl overflow-hidden"
								url={url}
								// muted={muted}
								playing={isPlaying}
								// onClick={() => setMuted(!muted)}
								loop={true}
								onEnded={onEnded}
							/>
							
							{/* Play/Pause Button Overlay */}
							{/*<button*/}
							{/*	onClick={onPlayClick}*/}
							{/*	className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition rounded-xl"*/}
							{/*>*/}
							{/*	{isPlaying ? (*/}
							{/*		<PauseIcon className="w-12 h-12 text-gray-400"/>*/}
							{/*	) : (*/}
							{/*		<PlayArrowIcon className="w-12 h-12 text-gray-400"/>*/}
							{/*	)}*/}
							{/*</button>*/}
						</div>
						{/*</MaximizableView>*/}
					</div>
				</Box>
			
			</div>
		</>
	);
}
